import { useEffect, useRef, useState } from 'react';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import {
  getUserPreferredThemeSetter,
  getIsDarkModeOn
} from '@stores/selectors/darkModeStatusSelectors';
import { submitLiveClassReview } from '@api/fetchers/class/updaters';
import useURLs from '@api/hooks/useURLs';
import { getUnLiveUrl } from '@api/selectors/urls';
import { useToggleable as useDrawer } from '@stores/toggleables';
import { DRAWER_IDS, sessionStates } from '@constants/index';
import getParamsFromURL from '@utils/getParamsFromURL';
import usePlayerReferralDialogStore from '@stores/playerReferralDialog';
import { getSetShowReferralDialog } from '@stores/selectors/playerReferralDialog';
import { readCookie } from '@utils/cookies';
import segmentStorage from '@events/segmentStorage';
import usePostClassRecommendation from '@stores/postClassRecommendation';
import { POST_CLASS_RECOMMENDATION_EXP } from '@cont/ClassVideoPlayer/constants';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { useSelectedGoalUID } from '@apihooks/topology/useSelectedGoal';
import useMyInfo from '@apihooks/auth/useMyInfo';
import { getIsFreeUser, getJWTToken } from '@apiselectors/auth';
import { abVariations } from '@utils/abHelper';
import useConstants from '@apihooks/useConstants';
import { getIsPostClassRecommendationEnabled } from '@apiselectors/constants';
import {
  getClassProcessedData,
  getStreamURL,
  getNextLessonURL,
  isClassroom,
  navigateToHome,
  browserBack
} from '../utils';
import usePlayerStore from './usePlayerStore';
import usePlayerEvents from './usePlayerEvents';
import useSegmentAnalytics from './useSegmentAnalytics';

function cleanupSegmentData() {
  segmentStorage.deleteItem('lpss');
}

const useClass = ({ iframeRef, internalFrame }) => {
  const setUserPreferredTheme = useDarkModeStatus(getUserPreferredThemeSetter);

  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const accessToken = readCookie('accessToken');
  const unLiveURL = useURLs(getUnLiveUrl);

  const {
    isPlayerLoaded,
    currentClassData,
    setPlayerLoaded,
    setCurrentClassData,
    setIsClassPage,
    isClassPage
  } = usePlayerStore();

  const { closeElement: closePlayer, isOpen: isPlayerOpen } = useDrawer(
    DRAWER_IDS.COURSE_CLASS_PLAYER
  );

  const [forceRenderCounter, setForceRenderCounter] = useState(0);
  const [urlToLoad, setUrlToLoad] = useState('about:blank');
  const [processedClassDetails, setProcessedClassDetails] = useState({});

  const segmentAnalytics = useSegmentAnalytics();
  const {
    setupPlayer,
    setupClock,
    setupViewer,
    setWatchTime,
    setViewer,
    setupForUa,
    cleanupPlayerEvents,
    sendSubscriptionCardVisibleEventOnIframe,
    sendSubscriptionClickedOnIframe,
    clock,
    player
  } = usePlayerEvents();

  const selectedGoalUID = useSelectedGoalUID();
  const goalUID = currentClassData?.programme?.goal?.uid || selectedGoalUID;
  const meJwt = useMyInfo(getJWTToken);
  const isFreeUser = useMyInfo(getIsFreeUser(goalUID));
  const postClassExperimentVariation = useRef(null);
  const isPostClassRecommendationEnabled = useConstants(
    getIsPostClassRecommendationEnabled(goalUID)
  );
  const { open: openPostClassRecommendationDialog } =
    usePostClassRecommendation();

  const [isExternalParam, CLXVersionParam, JWTToken, isSyllabusFeedParam] =
    getParamsFromURL(
      typeof window === 'undefined' ? '' : window?.location?.href,
      ['external', 'clxVersion', 'ext_token', 'isSyllabusFeed']
    );

  const isExternal = isExternalParam === '1';
  const isSyllabusFeed = isSyllabusFeedParam === 'true';

  async function openPostClassRecommendation(is5minSV) {
    // Opening post class recommendation dialog on class exit on following conditions:
    // - post class is enabled for the goal (isPostClassRecommendationEnabled)
    // - user don't have subscription for the class goal (isFreeUser)
    // - user lies in the test bucket
    // - class is a special class
    if (
      isPostClassRecommendationEnabled &&
      isFreeUser &&
      postClassExperimentVariation.current === abVariations.TEST_1 &&
      currentClassData?.programme?.isSpecial
    ) {
      openPostClassRecommendationDialog(
        goalUID,
        currentClassData.uid,
        is5minSV
      );
    }
  }

  const forwardDataToIframe = (data) => {
    iframeRef.current?.contentWindow?.postMessage(data, '*');
  };

  const handleCloseIframe = () => {
    forwardDataToIframe({
      iframeClosed: true
    });
  };

  const handleStartAnimationForIframe = () => {
    forwardDataToIframe({
      iframeMoved: true
    });
  };

  const sendDataToIframe = (data) => {
    forwardDataToIframe({
      lessonData: data,
      isDarkModeOn,
      apiHost: process.env.BACKEND_URL.slice(0, -1),
      isEmbeddedPlayer: internalFrame,
      shouldAutoplay: !internalFrame,
      isClassPage
    });
  };

  const sendClassData = () => {
    const data = {
      accessToken,
      data: processedClassDetails,
      showBackButton: true,
      role: 'listener',
      user_name: '',
      explicitJWTToken: JWTToken,
      isExternal,
      syllabusFeed: isSyllabusFeed,
      isClassPage
    };

    if (
      processedClassDetails.videoUrl &&
      processedClassDetails.state === sessionStates.PAST
    ) {
      sendDataToIframe({
        ...data,
        replay: 'true',
        url: processedClassDetails.videoUrl
      });
    } else {
      sendDataToIframe(data);
    }
  };

  const handlePlayerClose = () => {
    closePlayer();
    setProcessedClassDetails({});
    setCurrentClassData({});
    cleanupPlayerEvents();
  };

  const forceRender = () => {
    handleCloseIframe();
    handlePlayerClose();

    setForceRenderCounter((counter) => counter + 1);
    browserBack();
  };

  const onLoadHandler = () => {
    // precautionary cleanups
    cleanupPlayerEvents();

    // player/clock instance already there, reset them with new data
    setupPlayer(processedClassDetails);
    setupClock();
    setupViewer();

    // sequence methods
    handleStartAnimationForIframe();
    sendClassData();
    setWatchTime();
    setViewer();
    setupForUa();
  };

  const setShowPlayerReferralDialog = usePlayerReferralDialogStore(
    getSetShowReferralDialog
  );

  const handleShowReferral = () => {
    setShowPlayerReferralDialog(currentClassData.uid);
  };

  const onBackCleanup = () => {
    cleanupSegmentData();
    handleShowReferral();
    handleCloseIframe();
    handlePlayerClose();
  };

  const handleOnPlayerBack = () => {
    onBackCleanup();

    const route = window.location.pathname;

    if (isClassroom(route)) {
      navigateToHome();
      return;
    }

    browserBack();
  };

  useEffect(() => {
    if (currentClassData.uid) {
      const formattedData = getClassProcessedData(currentClassData);

      const streamURL = getStreamURL(
        formattedData,
        unLiveURL,
        isExternal,
        CLXVersionParam
      );

      if (urlToLoad !== streamURL) {
        setUrlToLoad(streamURL);
        setPlayerLoaded(false);
      }

      setProcessedClassDetails(formattedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClassData.uid, unLiveURL]);

  useEffect(() => {
    if (processedClassDetails.uid && isPlayerLoaded) {
      onLoadHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlayerLoaded, processedClassDetails.uid]);

  useEffect(() => {
    if (!isPlayerOpen) {
      return;
    }

    const onMessageReceivedHandler = (e) => {
      if (e.data.subscriptionCardVisibleOnIframe) {
        sendSubscriptionCardVisibleEventOnIframe();
      }

      if (e.data.getSubscriptionClickedOnIframe) {
        sendSubscriptionClickedOnIframe();
      }

      if (e.data.liveClassReady) {
        setPlayerLoaded(true);
      }

      if (e.data.isFeedbackData) {
        submitLiveClassReview(e.data.feedbackData);
      }

      if (e.data.closeIframe) {
        openPostClassRecommendation(e.data.isFiveMinSV);
        handleOnPlayerBack();
      }

      if (e.data.isNextLessonClicked) {
        const updatedData = {
          ...e.data.nextLesson,
          type: 'post'
        };

        const urlToOpen = getNextLessonURL(updatedData, true);

        window.open(urlToOpen, '_self');
      }

      if (e.data.reloadIframe) {
        forceRender();
      }

      if (e.data.setDarkModeOn) {
        setUserPreferredTheme(e.data.setDarkModeOn.shouldSet);
      }

      if (e.data.segmentEvents) {
        const player_id = player?.watchEvent.PLAYER_ID;

        if (player_id) {
          e.data.eventData.data.player_id = player_id;
        }

        segmentAnalytics(e.data.eventData);
      }

      if (e.data.forwardToCLX) {
        forwardDataToIframe(e.data);
      }
    };

    window.addEventListener('message', onMessageReceivedHandler);

    return () => {
      window.removeEventListener('message', onMessageReceivedHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlayerOpen]);

  useEffect(() => {
    if (!isPlayerOpen) {
      return;
    }

    const handleOnPopState = () => {
      openPostClassRecommendation(true);
      onBackCleanup();
    };

    window.addEventListener('popstate', handleOnPopState, false);

    return () => {
      window.removeEventListener('popstate', handleOnPopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlayerOpen]);

  useEffect(() => {
    return () => {
      handlePlayerClose();
      cleanupPlayerEvents();
      setIsClassPage(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetching AB experiment data for post class recommendation on class player load
    // Only fetching the AB experiment details if the post class recommendations need to be shown
    if (
      isPostClassRecommendationEnabled &&
      isFreeUser &&
      currentClassData?.programme?.isSpecial
    ) {
      fetchABExperimentData(POST_CLASS_RECOMMENDATION_EXP, meJwt).then(
        (res) => {
          postClassExperimentVariation.current = res.variation;
        }
      );
    }
  }, [currentClassData, isFreeUser, isPostClassRecommendationEnabled, meJwt]);

  return {
    key: `${urlToLoad}_${forceRenderCounter}`,
    urlToLoad,
    currentClassData,
    clock,
    onBackCleanup
  };
};

export default useClass;
